import { useRef } from "react";
import type { DOMAttributes } from "react";

/** Toque longo: 450ms parado (o mesmo tempo do WhatsApp/Telegram). */
const LONG_PRESS_MS = 450;
/** Folga do dedo parado. Acima disso e rolagem, nao toque longo. */
const LONG_PRESS_SLOP_PX = 10;
/** Distancia minima do arraste para contar como troca de item. */
const SWIPE_PX = 56;

/** Handlers prontos para espalhar no elemento (`{...useLongPress(fn)}`). */
type TouchHandlers = Pick<
  DOMAttributes<HTMLElement>,
  "onTouchStart" | "onTouchMove" | "onTouchEnd" | "onTouchCancel" | "onContextMenu"
>;

/**
 * Toque longo (dedo) com cancelamento por movimento. Devolve os handlers para
 * espalhar no elemento; com `onLongPress` ausente todos saem `undefined`, e o
 * elemento fica sem nenhum listener (desktop segue com o menu nativo).
 *
 * Detalhe ajustado ao vivo e que nao pode se perder: ROLAR cancela o timer —
 * senao a folha de acoes abre no meio de um arraste da timeline.
 */
export function useLongPress(onLongPress?: () => void): TouchHandlers {
  const timer = useRef(0);
  const from = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const cancel = (): void => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = 0;
    from.current = null;
  };

  if (!onLongPress) return {};

  return {
    onTouchStart: (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      fired.current = false;
      from.current = { x: touch.clientX, y: touch.clientY };
      timer.current = window.setTimeout(() => {
        fired.current = true;
        cancel();
        onLongPress();
      }, LONG_PRESS_MS);
    },
    onTouchMove: (event) => {
      const touch = event.touches[0];
      const start = from.current;
      if (!touch || !start) return;
      if (
        Math.abs(touch.clientX - start.x) > LONG_PRESS_SLOP_PX ||
        Math.abs(touch.clientY - start.y) > LONG_PRESS_SLOP_PX
      ) {
        cancel();
      }
    },
    onTouchEnd: cancel,
    onTouchCancel: cancel,
    // So engole o menu nativo quando a folha ja abriu; no desktop o botao
    // direito continua normal.
    onContextMenu: (event) => {
      if (fired.current) event.preventDefault();
    },
  };
}

/**
 * Arraste horizontal (trocar de item). `onSwipe(1)` = arrastou para a
 * esquerda (proximo), `onSwipe(-1)` = para a direita (anterior).
 *
 * Detalhe ajustado ao vivo: o gesto so conta quando o deslocamento horizontal
 * vence o vertical — rolar na vertical (ex. a legenda da foto) nao pode trocar
 * de item.
 */
export function useSwipe(onSwipe: (delta: 1 | -1) => void): TouchHandlers {
  const from = useRef<{ x: number; y: number } | null>(null);

  return {
    onTouchStart: (event) => {
      const touch = event.touches[0];
      from.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
    },
    onTouchEnd: (event) => {
      const start = from.current;
      const touch = event.changedTouches[0];
      from.current = null;
      if (!start || !touch) return;
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) <= Math.abs(dy)) return;
      onSwipe(dx < 0 ? 1 : -1);
    },
  };
}
